import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { classService, getErrorMessage, MIN_PASSWORD_LENGTH } from '../services/api';
import type { ClassInvitePreview } from '../types/api';
import AuthShell from '../components/AuthShell';
import { Spinner } from '../components/ui';
import { siteUrl } from '../utils/site';
import { useToast } from '../components/Toast';
import {
  clearPendingInviteCode,
  getPendingInviteCode,
  normalizeInviteCode,
  setPendingInviteCode,
} from '../utils/invite';

// A department or a whole university: a conversation (PLAN-5).
const SALES_EMAIL = 'contact@webots.academy';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [searchParams] = useSearchParams();
  // /signup?role=professor (the landing page's "for professors" link) opens with Teacher selected.
  const requestedRole = searchParams.get('role');
  const [role, setRole] = useState<'teacher' | 'student'>(requestedRole === 'professor' || requestedRole === 'teacher' ? 'teacher' : 'student');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  // From the website's "Get the Student plan" (PLAN-13): the plan is bought after the account exists.
  const wantsStudentPlan = searchParams.get('plan') === 'student';
  const [inviteClass, setInviteClass] = useState<ClassInvitePreview | null>(null);
  const { user, loading: authLoading, register, login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryInviteCode = normalizeInviteCode(searchParams.get('invite'));
  const storedInviteCode = getPendingInviteCode();
  const inviteCode = useMemo(() => queryInviteCode || storedInviteCode, [queryInviteCode, storedInviteCode]);
  const isInviteFlow = !!inviteCode;

  useEffect(() => {
    if (queryInviteCode) {
      setPendingInviteCode(queryInviteCode);
    }
  }, [queryInviteCode]);

  useEffect(() => {
    if (isInviteFlow) {
      setRole('student');
    }
  }, [isInviteFlow]);

  // Name the class being joined, so the student knows the link is the right one.
  useEffect(() => {
    if (!inviteCode) {
      setInviteClass(null);
      return;
    }
    let cancelled = false;
    classService.getClassByInviteCode(inviteCode)
      .then((preview) => { if (!cancelled) setInviteClass(preview); })
      .catch(() => { if (!cancelled) setInviteClass(null); });
    return () => { cancelled = true; };
  }, [inviteCode]);

  if (user && !authLoading && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`The password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const registrationRole = inviteCode ? 'student' : role;
      const registered = await register(email, fullName, password, registrationRole, newsletterOptIn, inviteCode);
      if (!registered.email_confirmed_at) {
        // ACC-16: the account works once its address is confirmed. A pending invite stays stored (and rides in the
        // emailed link), so it is followed at the first login.
        navigate('/check-email', { state: { email: registered.email, sent: true } });
        return;
      }
      // Already confirmed (a local stack's test accounts): straight in, as asking for the password again right after
      // choosing it only loses people.
      const loggedInUser = await login(email, password);

      if (inviteCode) {
        if (loggedInUser.role === 'student') {
          try {
            await classService.joinClassByInviteCode(inviteCode);
          } catch (joinError) {
            toast.error(`Registration succeeded, but joining the class failed: ${getErrorMessage(joinError, 'Unable to join class from invite link.')}`);
          } finally {
            clearPendingInviteCode();
          }
        }
      }
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const roleButtonClasses = (active: boolean) => `flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${active
    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200 dark:bg-gray-700 dark:text-white dark:ring-gray-600'
    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
    }`;

  return (
    <AuthShell>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Register</h2>
      <p className="mt-1 mb-6 text-sm text-gray-500 dark:text-gray-400">Program real robots in a simulator, right in your browser.</p>

      <div className="space-y-3 mb-5 empty:hidden">
        {error && (
          <div className="alert alert-error" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        {isInviteFlow && (
          <div className="alert alert-info">
            <Info />
            <span>
              You are registering through a class invite link{inviteClass ? ' to join ' : '. '}
              {inviteClass && <><strong className="font-semibold">{inviteClass.name}</strong>. </>}
              Your student account will be enrolled automatically.
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="register-name" className="label">Full Name</label>
          <input
            id="register-name"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="input"
            autoComplete="name"
            required
          />
        </div>

        {!isInviteFlow && (
          <div>
            <span id="register-role-label" className="label">I am a:</span>
            <div role="group" aria-labelledby="register-role-label" className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
              <button type="button" onClick={() => setRole('student')} aria-pressed={role === 'student'} className={roleButtonClasses(role === 'student')}>
                Student
              </button>
              <button type="button" onClick={() => setRole('teacher')} aria-pressed={role === 'teacher'} className={roleButtonClasses(role === 'teacher')}>
                Teacher
              </button>
            </div>
          </div>
        )}

          <div>
            <label htmlFor="register-email" className="label">Email</label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label htmlFor="register-password" className="label">Password</label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              aria-describedby="password-hint"
              required
            />
            <p id="password-hint" className="hint">At least {MIN_PASSWORD_LENGTH} characters.</p>
          </div>

          <div>
            <label htmlFor="register-password-confirm" className="label">Confirm Password</label>
            <input
              id="register-password-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              required
            />
          </div>

          {/* Teachers sign up themselves (ACC-3): free evaluation first, then the Class plan per class (PLAN-3, PLAN-4). */}
          {role === 'teacher' && !isInviteFlow && (
            <div className="alert alert-info">
              <Info />
              <span>
                Start free with one evaluation class of up to 3 students. When you're ready to teach, activate the Class
                plan for your class from your dashboard: €890 per semester, up to 30 students, each with 1 hour of simulation a
                day for the class's lessons. For a whole department,{' '}
                <a className="link" href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Webots Academy for our department')}`}>write to us</a>.
              </span>
            </div>
          )}
          {wantsStudentPlan && role === 'student' && (
            <div className="alert alert-info">
              <Info />
              <span>Create your free account first; you can then get the Student plan (€9/month, 1 hour of simulation a day) from your dashboard.</span>
            </div>
          )}
          <div className="space-y-3 pt-1">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                id="accept-privacy"
                type="checkbox"
                checked={acceptedPrivacyPolicy}
                onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
                className="checkbox mt-0.5"
                required
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {/* ACC-2, WEB-10: creating an account accepts the terms of service. */}
                I agree to the{' '}
                <a href={siteUrl('/terms')} target="_blank" rel="noopener noreferrer" className="link">
                  Terms of Service
                </a>{' '}
                and the{' '}
                <a href={siteUrl('/privacy')} target="_blank" rel="noopener noreferrer" className="link">
                  Privacy Policy
                </a>
              </span>
            </label>
            {/* Unticked: newsletter consent must be an active choice (GDPR). */}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                id="newsletter-opt-in"
                type="checkbox"
                checked={newsletterOptIn}
                onChange={(e) => setNewsletterOptIn(e.target.checked)}
                className="checkbox mt-0.5"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Send me the Webots Academy newsletter: new lessons and features, about once a month. You'll confirm by email and can unsubscribe anytime.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !acceptedPrivacyPolicy}
            className="btn btn-primary btn-lg w-full !mt-6"
          >
            {loading && <Spinner className="w-4 h-4 text-white" />}
            {loading ? 'Registering...' : `Register as ${role === 'teacher' && !isInviteFlow ? 'Teacher' : 'Student'}`}
          </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Already have an account?{' '}
        <Link to={inviteCode ? `/login?invite=${inviteCode}` : '/login'} className="link">
          Login
        </Link>
      </p>
    </AuthShell>
  );
};

export default Register;
